import { configureStore, combineReducers } from "@reduxjs/toolkit";
import shipBuilderReducer from "./features/shipBuilderSlice";
import optionalSystemsReducer from "./features/optionalSystemsSlice";
import uiReducer from "./features/uiSlice";
import breakawayHullReducer from "./features/breakawayHullSlice";
import moduleReducer from "./features/moduleSlice";
import crewReducer from "../domains/crew/crewSlice";
import hullReducer from "../domains/hull/hullSlice";
import powerReducer from "../domains/power/powerSlice";
import drivesReducer from "../domains/drives/drivesSlice";
import weaponsReducer from "../domains/weapons/weaponsSlice";
import { loadState, saveState } from "../utils/cache";
import { ShipBuilderState, ShipStats } from "../types/ship";
import { OptionalSystemsState } from "../types/optionalSystems";
import { UIState } from "./types/ui";
import { BreakawayHullState } from "../types/breakawayHull";
import { ModuleState } from "./features/moduleSlice";
import { CrewState } from "../domains/crew/types";
import { HullState } from "../domains/hull/types";
import { PowerState } from "../domains/power/types";
import { DrivesState } from "../domains/drives/types";
import { WeaponsState } from "../domains/weapons/types";
import hullAndArmorMiddleware from "./middleware/hullAndArmorMiddleware";
import shipStatsMiddleware from "./middleware/shipStatsMiddleware";
import driveStatsMiddleware from "./middleware/driveStatsMiddleware";
import drivePowerPlantMiddleware from "./middleware/drivePowerPlantMiddleware";
import breakawayDriveSyncMiddleware from "./middleware/breakawayDriveSyncMiddleware";
import weaponPowerPlantMiddleware from "./middleware/weaponPowerPlantMiddleware";
import sensorPowerPlantMiddleware from "./middleware/sensorPowerPlantMiddleware";
import stateroomPowerPlantMiddleware from "./middleware/stateroomPowerPlantMiddleware";
import optionalSystemsPowerPlantMiddleware from "./middleware/optionalSystemsPowerPlantMiddleware";
import { stateroomAutoAssignMiddleware } from "./middleware/stateroomAutoAssignMiddleware";
import { crewAutoAssignMiddleware } from "./middleware/crewAutoAssignMiddleware";
import loggingMiddleware from "./middleware/loggingMiddleware";
import { calculateShipStats } from "../utils/calculations/shipCalculations";
import { updateShipStats } from "./features/shipBuilderSlice";
import { defaultSettings } from "./features/shipBuilderSlice";

const rootReducer = combineReducers({
  shipBuilder: shipBuilderReducer,
  optionalSystems: optionalSystemsReducer,
  ui: uiReducer,
  breakawayHull: breakawayHullReducer,
  module: moduleReducer,
  crew: crewReducer,
  hull: hullReducer,
  power: powerReducer,
  drives: drivesReducer,
  weapons: weaponsReducer,
});

export type RootState = {
  shipBuilder: ShipBuilderState;
  optionalSystems: OptionalSystemsState;
  ui: UIState;
  breakawayHull: BreakawayHullState;
  module: ModuleState;
  crew: CrewState;
  hull: HullState;
  power: PowerState;
  drives: DrivesState;
  weapons: WeaponsState;
};

// Load cached state and ensure it has all required properties
const cachedState = loadState() as Partial<RootState>;

// Validate cached state to ensure it's not corrupted
const isValidCachedState = cachedState.shipBuilder && 
  cachedState.shipBuilder.shipSize > 0 && 
  cachedState.shipBuilder.hull;

const preloadedState: Partial<RootState> = {
  ...(isValidCachedState ? cachedState : {}),
  shipBuilder: {
    ...(isValidCachedState ? cachedState.shipBuilder : {}),
    settings: {
      ...defaultSettings,
      ...(cachedState.shipBuilder?.settings || {})
    },
    smallCraft: cachedState.shipBuilder?.smallCraft || [],
    smallCraftCount: cachedState.shipBuilder?.smallCraftCount || 0,
  } as any,
  breakawayHull: cachedState.breakawayHull || {
    isBreakaway: false,
    sections: [],
    totalTonnage: 0,
    totalCost: 0,
    separationTime: "1D rounds",
    combinedPerformance: {
      maneuver: 0,
      jump: 0,
      reaction: 0,
      totalPower: 0,
      totalFuel: 0,
      totalCost: 0,
      totalArmorWeight: 0,
      totalArmorCost: 0,
    },
  },
  module: cachedState.module || {
    currentStep: 0,
    moduleData: null,
    moduleConfig: null,
    validation: {
      isValid: false,
      errors: [],
      warnings: [],
    },
    isDirty: false,
    lastSaved: null,
    version: "1.0.0",
  },
};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      hullAndArmorMiddleware,
      drivePowerPlantMiddleware, // Move before shipStatsMiddleware so power plant updates trigger stats recalculation
      sensorPowerPlantMiddleware, // Move before shipStatsMiddleware so sensor power plant updates trigger stats recalculation
      shipStatsMiddleware,
      driveStatsMiddleware,
      breakawayDriveSyncMiddleware,
      weaponPowerPlantMiddleware,
      crewAutoAssignMiddleware, // Auto-assign crew when requirements change
      stateroomAutoAssignMiddleware,
      stateroomPowerPlantMiddleware,
      optionalSystemsPowerPlantMiddleware,
      loggingMiddleware,
    ),
  preloadedState,
});

let lastStats: ShipStats | null = null;
let isInitialized = false;

// Initial sync of breakaway drives if they exist
const initialState = store.getState();
if (initialState.breakawayHull?.isBreakaway && initialState.breakawayHull.sections?.length > 0) {
  console.log("Initial sync of breakaway drives");
  // This will be handled by the middleware on the next action
}

store.subscribe(() => {
  const state = store.getState();

  // Skip calculation if state is not properly initialized
  if (!isInitialized) {
    if (state.shipBuilder.shipSize > 0 && state.shipBuilder.hull) {
      isInitialized = true;
    } else {
      return; // Skip calculation until state is properly initialized
    }
  }

  // Add debugging to see what the state values are
  if (!state.shipBuilder.shipSize || state.shipBuilder.shipSize <= 0) {
    console.warn("Ship size is invalid:", {
      shipSize: state.shipBuilder.shipSize,
      hull: state.shipBuilder.hull,
      currentStep: state.shipBuilder.currentStep
    });
    return; // Skip calculation if ship size is invalid
  }

  const result = calculateShipStats(state.shipBuilder, state.optionalSystems);
  if (result.success && result.data) {
    // Only update if stats have changed
    if (JSON.stringify(result.data) !== JSON.stringify(lastStats)) {
      lastStats = result.data;
      store.dispatch(updateShipStats(result.data));
    }
  } else if (!result.success) {
    console.error("Ship stats calculation failed:", result.error);
    console.error("Current state:", {
      shipSize: state.shipBuilder.shipSize,
      hull: state.shipBuilder.hull,
      currentStep: state.shipBuilder.currentStep
    });
  }
  saveState({
    shipBuilder: state.shipBuilder,
    optionalSystems: state.optionalSystems,
    breakawayHull: state.breakawayHull,
    module: state.module,
    ui: state.ui,
  });
});

export type AppDispatch = typeof store.dispatch;

export { store };

// Re-export hooks
export { useAppDispatch, useAppSelector } from "./hooks";

// Re-export actions from shipBuilderSlice
export {
  updateShipStats,
  setHull,
  setDrive,
  setPowerPlant,
  setBridge,
  setComputer,
  setPrimaryComputer,
  setBackupComputer,
  setSensors,
  setArmor,
  setTechLevel,
  setShipSize,
  setCurrentStep,
  setCargoSpace,
  setCrewComposition,
  setFuelTanks,
  setShipDetails,
  setSoftware,
  setStateroomConfig,
  setStateroomAutoAssign,
  setStateroomPercentage,
  setWeapons,
  setCargo,
  setFuel,
  setStaterooms,
  setCrew,
  setShipName,
  setShipClass,
  setShipDescription,
  addOptionalSystem,
  removeOptionalSystem,
  addWeapon,
  removeWeapon,
  resetShip,
  addCrewMember,
  removeCrewMember,
  updateCrewMember,
  toggleOfficer,
  markStepVisited,
  installSoftware,
  uninstallSoftware,
  setSoftwareLevel,
  startSoftware,
  stopSoftware,
  addAmmunition,
  removeAmmunition,
  updateAmmunitionQuantity,
  loadShipData,
  loadFullState,
} from "./features/shipBuilderSlice";

// Re-export actions from optionalSystemsSlice
export {
  addStructureSystem,
  removeStructureSystem,
  addModularHullSystem,
  removeModularHullSystem,
  addPowerSystem,
  removePowerSystem,
  addDriveSystem,
  removeDriveSystem,
  addFuelSystem,
  removeFuelSystem,
  addAccommodationSystem,
  removeAccommodationSystem,
  addBridgeSystem,
  removeBridgeSystem,
  addCargoSystem,
  removeCargoSystem,
  addDroneSystem,
  removeDroneSystem,
  addSensorSystem,
  removeSensorSystem,
  addExternalSystem,
  removeExternalSystem,
  addInternalSystem,
  removeInternalSystem,
  addSoftwareSystem,
  removeSoftwareSystem,
  resetOptionalSystems,
  removeCommonAreaSystem,
} from "./features/optionalSystemsSlice";

// Re-export actions from uiSlice
export {
  setUICurrentStep,
  setIsDirty,
  setErrors,
  setWarnings,
  setIsStepComplete,
  clearErrors,
  setIsSidebarOpen,
  setSelectedSystem,
  setValidationErrors,
  setValidationWarnings,
  toggleDarkMode,
  setDarkMode,
} from "./features/uiSlice";
